import { BannerMessageType } from '@ridango/components';
import {
    BANNER_ACTION_MORE_KEY,
    BANNER_NOTIFICATION_KEY,
    BANNER_TYPE_DISTURBANCE_KEY,
    BANNER_TYPE_INFO_KEY,
    BANNER_TYPE_NOTIFICATION_KEY,
    BANNER_TYPE_TRAFFIC_INFO_KEY,
    BANNER_TYPE_WARNING_KEY,
    DEFAULT_FALLBACK_MAX_HEIGHT,
    DEFAULT_LINE_COUNT
} from './banner.constants';
import {
    BannerAriaLabelOptions,
    BannerStyleFlags,
    BannerTitleResult,
    MeasureBannerTitleOptions,
    ResolveBannerTitleOptions
} from './banner.types';

export class BannerUtils {
    static getStyleFlags(type: BannerMessageType | undefined, isHighContrast: boolean): BannerStyleFlags {
        const isNormalMode = !isHighContrast;

        return {
            infoStyle: isNormalMode && (type === BannerMessageType.Info || type === BannerMessageType.Disturbance),
            trafficInfoStyle: isNormalMode && type === BannerMessageType.TrafficInfo,
            warningStyle: isNormalMode && type === BannerMessageType.Warning,
            highContrastStyle: isHighContrast
        };
    }

    static getTypeTranslationKey(type: BannerMessageType | undefined): string {
        switch (type) {
            case BannerMessageType.Info:
                return BANNER_TYPE_INFO_KEY;
            case BannerMessageType.Disturbance:
                return BANNER_TYPE_DISTURBANCE_KEY;
            case BannerMessageType.TrafficInfo:
                return BANNER_TYPE_TRAFFIC_INFO_KEY;
            case BannerMessageType.Warning:
                return BANNER_TYPE_WARNING_KEY;
            default:
                return BANNER_TYPE_NOTIFICATION_KEY;
        }
    }

    static buildAriaLabel({ type, title, translate }: BannerAriaLabelOptions): string {
        const notificationLabel = translate(BANNER_NOTIFICATION_KEY);
        const typeLabel = translate(BannerUtils.getTypeTranslationKey(type));
        const actionLabel = translate(BANNER_ACTION_MORE_KEY);

        return [notificationLabel, typeLabel, title, actionLabel].filter(Boolean).join(', ');
    }

    static resolveTitle({ sourceTitle, moreLabel, fits }: ResolveBannerTitleOptions): BannerTitleResult {
        const fullText = `${sourceTitle} ${moreLabel}`;
        if (fits(fullText)) {
            return {
                renderedTitle: sourceTitle,
                isTitleTruncated: false
            };
        }

        const suffix = `... ${moreLabel}`;
        const trimmedSource = sourceTitle.trim();

        let low = 0;
        let high = trimmedSource.length;
        let best = '';

        while (low <= high) {
            const mid = Math.floor((low + high) / 2);
            const candidate = trimmedSource.slice(0, mid).trimEnd();
            const candidateText = `${candidate}${suffix}`;

            if (candidate.length > 0 && fits(candidateText)) {
                best = candidate;
                low = mid + 1;
            } else {
                high = mid - 1;
            }
        }

        if (best) {
            return {
                renderedTitle: best,
                isTitleTruncated: true
            };
        }

        return {
            renderedTitle: sourceTitle,
            isTitleTruncated: false
        };
    }

    static getTextMaxHeight(
        lineHeight: string,
        lineCount = DEFAULT_LINE_COUNT,
        fallback = DEFAULT_FALLBACK_MAX_HEIGHT
    ): number {
        const parsedLineHeight = Number.parseFloat(lineHeight);
        return Number.isFinite(parsedLineHeight) && parsedLineHeight > 0 ? parsedLineHeight * lineCount : fallback;
    }

    static createMeasureElement(titleElement: HTMLElement): HTMLElement | null {
        const parent = titleElement.parentElement;
        if (!parent) {
            return null;
        }

        const measureElement = document.createElement('p');
        measureElement.className = titleElement.className;
        measureElement.style.position = 'absolute';
        measureElement.style.top = '0';
        measureElement.style.right = '0';
        measureElement.style.bottom = '0';
        measureElement.style.left = '0';
        measureElement.style.visibility = 'hidden';
        measureElement.style.pointerEvents = 'none';
        measureElement.style.maxHeight = `${DEFAULT_FALLBACK_MAX_HEIGHT}px`;
        measureElement.style.overflow = 'hidden';
        parent.appendChild(measureElement);

        return measureElement;
    }

    static measureTitle({ titleElement, sourceTitle, moreLabel }: MeasureBannerTitleOptions): BannerTitleResult {
        if (!titleElement) {
            return {
                renderedTitle: sourceTitle,
                isTitleTruncated: false
            };
        }

        const measureElement = BannerUtils.createMeasureElement(titleElement);
        if (!measureElement) {
            return {
                renderedTitle: sourceTitle,
                isTitleTruncated: false
            };
        }

        try {
            const maxHeight = BannerUtils.getTextMaxHeight(window.getComputedStyle(measureElement).lineHeight);

            const fits = (text: string): boolean => {
                measureElement.textContent = text;
                return measureElement.scrollHeight <= maxHeight;
            };

            return BannerUtils.resolveTitle({
                sourceTitle,
                moreLabel,
                fits
            });
        } finally {
            measureElement.remove();
        }
    }
}
